import React, { useEffect, useState } from "react";
import classes from "./Courses.module.scss";
import CustomImage from "../../CustomImage";
import useEmblaCarousel from "embla-carousel-react";
import useIsMobile from "../../../hooks/useIsMobile";

const COLUMNS = 2;
const ROWS = 4;

const COLUMNS_FOR_MOBILE = 1;
const ROWS_FOR_MOBILE = 5;

function chunkCourses<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function getPopularCoursesLists(countryData: any, tabActive: string) {
  const top = countryData?.topCourses?.tabs ?? [];
  const rootTopCourses = top?.find((tab: any) => tab?.title === tabActive)?.courses;

  return Array.isArray(rootTopCourses) && rootTopCourses.length > 0 ? rootTopCourses : [];
}

const Courses = ({ countryData }: { countryData: any }) => {
  const isMobile = useIsMobile(768);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });

  const [coursesData, setCoursesData] = useState<any>([]);

  const [tabActive, setTabActive] = useState(countryData?.topCourses?.tabs?.[0]?.title ?? "");
  const [tabsArr, setTabsArr] = useState([]);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    const popularCoursesData = getPopularCoursesLists(countryData, tabActive);

    const courseSlides = chunkCourses(popularCoursesData, isMobile ? COLUMNS_FOR_MOBILE * ROWS_FOR_MOBILE : COLUMNS * ROWS);

    setCoursesData(courseSlides ?? []);

    setTabsArr(countryData?.topCourses?.tabs ?? []);
  }, [countryData, tabActive, isMobile]);

  useEffect(() => {
    if (!emblaApi) return;
    const updateScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
    };
  }, [emblaApi, coursesData]);

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();

  const filterClickHandler = (filter: string) => {
    setTabActive(filter);
    emblaApi?.scrollTo(0);
  };

  const hasPriorityBanner = Boolean(countryData?.banner_1?.priorityImage?.url);

  return (
    <div id="courses" className={`${classes["courses-container"]}${hasPriorityBanner ? ` ${classes["courses-container--priority-banner"]}` : ""}`}>
      <h2 className={classes["courses-title"]}>{countryData?.topCourses?.title ?? ""}</h2>
      <div className={classes["courses-description"]} dangerouslySetInnerHTML={{ __html: countryData?.topCourses?.description ?? "" }}></div>

      <div className={classes["section-btn-container"]}>
        {tabsArr?.length > 0 &&
          tabsArr?.map((tab: any) => (
            <button
              key={tab?.id}
              className={tabActive === tab?.title ? `${classes["bachelors-btn"]} ${classes["active-btn"]}` : `${classes["bachelors-btn"]}`}
              onClick={() => filterClickHandler(tab?.title)}
            >
              {tab?.title}
            </button>
          ))}
      </div>

      {isMobile ? (
        <>
          <div className={classes["courses-card-container-mobile"]}>
            <div className={classes.viewport} ref={emblaRef}>
              <div className={classes["courses-card-slider-container"]}>
                {coursesData.map((chunk: any, slideIndex: number) => (
                  <div className={classes.courseSlide} key={`slide-${slideIndex}`}>
                    <div className={classes.courseGrid}>
                      {chunk.map((el: any, index: number) => (
                        <div key={index} className={classes.courseCard}>
                          {el ?? ""}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollPrev ? goToPrev : undefined}
              aria-disabled={!canScrollPrev}
            >
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>
            {/* RIGHT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollNext ? goToNext : undefined}
              aria-disabled={!canScrollNext}
            >
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={classes["courses-card-container"]}>
            <div className={classes.viewport} ref={emblaRef}>
              <div className={classes["courses-card-slider-container"]}>
                {coursesData.map((chunk: any, slideIndex: number) => (
                  <div className={classes.courseSlide} key={`slide-${slideIndex}`}>
                    <div className={classes.courseGrid}>
                      {chunk.map((el: any, index: number) => (
                        <div key={index} className={classes.courseCard}>
                          {el ?? ""}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollPrev ? goToPrev : undefined}
              aria-disabled={!canScrollPrev}
            >
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>
            {/* RIGHT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollNext ? goToNext : undefined}
              aria-disabled={!canScrollNext}
            >
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Courses;
